import React from 'react';
import { motion } from 'framer-motion';

const Terms = () => {
  const Section = ({ title, children }) => (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-[#1e293b]">{title}</h3>
      <div className="mt-2 text-sm leading-6 text-[#2A2D34]/80">{children}</div>
    </div>
  );

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#F5F7FA] to-[#E4E7EC] text-[#2A2D34] px-4 py-12">
      <div className="pointer-events-none fixed -top-24 -right-28 -z-10 h-80 w-80 rounded-full bg-[#10A7F3]/20 blur-3xl" />
      <div className="pointer-events-none fixed -bottom-32 -left-24 -z-10 h-96 w-96 rounded-full bg-[#10F3D6]/20 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="mx-auto w-full max-w-3xl"
      >
        <div className="mb-6 flex items-center justify-between">
          <a href="/" className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">Flowman</a>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-white/60 bg-white/80 p-6 shadow-xl backdrop-blur">
          <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-[#10A7F3]/10 to-[#10F3D6]/20 blur-2xl" />

          <div className="mb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#10A7F3]/30 bg-white/80 px-3 py-1 text-sm font-medium text-[#0D8ED3] shadow-sm">
              <span className="inline-block h-2 w-2 rounded-full bg-[#10A7F3]" />
              Legal
            </div>
            <h1 className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight text-[#1e293b]">Terms of Service</h1>
            <p className="mt-1 text-sm text-[#2A2D34]/70">Effective Date: August 30, 2025</p>
          </div>

          <Section title="1) Acceptance of Terms">
            <p>By creating an account, accessing, or using Flowman ("Service"), you agree to these Terms of Service ("Terms") and our Privacy Policy. If you use the Service on behalf of an organization, you represent that you have authority to bind that organization and "you" will refer to that organization.</p>
          </Section>

          <Section title="2) Eligibility and Accounts">
            <ul className="list-disc pl-5">
              <li>You must be at least 16 years old (or the age of digital consent in your locale) to use the Service.</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.</li>
              <li>Provide accurate, complete, and up-to-date information during registration and keep it current.</li>
            </ul>
          </Section>

          <Section title="3) Workspaces and Ownership">
            <ul className="list-disc pl-5">
              <li>Flowman enables collaboration via workspaces, folders, tasks, comments, and attachments.</li>
              <li>By default, the creator of a workspace (or the organization that owns the subscription) owns the content in that workspace.</li>
              <li>Workspace owners/admins may invite/remove users, assign roles, and set permissions. Your access may change based on workspace settings.</li>
            </ul>
          </Section>

          <Section title="4) Your Content and License">
            <ul className="list-disc pl-5">
              <li>You retain ownership of the content you upload ("User Content").</li>
              <li>You grant Flowman a worldwide, non-exclusive, royalty-free license to host, store, display, and process User Content solely to operate, improve, and provide the Service.</li>
              <li>You are responsible for User Content and ensuring you have rights to upload and share it.</li>
            </ul>
          </Section>

          <Section title="5) Acceptable Use">
            <ul className="list-disc pl-5">
              <li>Do not upload unlawful, harmful, infringing, defamatory, harassing, or hateful content.</li>
              <li>Do not upload regulated or sensitive data without appropriate safeguards and permissions.</li>
              <li>Do not probe, scan, or test vulnerabilities, or circumvent security and access controls.</li>
              <li>Do not interfere with the Service or deliver malware or denial-of-service attacks.</li>
              <li>Do not reverse engineer or attempt to extract source code except as permitted by law.</li>
            </ul>
          </Section>

          <Section title="6) Attachments and Integrations">
            <ul className="list-disc pl-5">
              <li>You are responsible for rights and compliance of files you upload.</li>
              <li>Third-party integrations are governed by their own terms and privacy policies.</li>
            </ul>
          </Section>

          <Section title="7) Notifications and Communications">
            <p>The Service may send transactional communications (e.g., invites, assignments, reminders). You can manage some notifications in settings; essential messages may be required to operate your account.</p>
          </Section>

          <Section title="8) Plans, Fees, and Changes">
            <p>Some features may be free; paid plans (if introduced) will include applicable billing terms. We may change features, introduce limits, or discontinue parts of the Service with reasonable notice when feasible.</p>
          </Section>

          <Section title="9) Service Availability and Support">
            <p>We aim for reliable availability but do not guarantee uninterrupted or error-free operation. Maintenance, updates, and network events may affect availability.</p>
          </Section>

          <Section title="10) Privacy and Data Security">
            <ul className="list-disc pl-5">
              <li>Privacy: See our Privacy Policy for details on how we collect and use data.</li>
              <li>Security: We use reasonable safeguards, but no method is completely secure. Use the Service at your own risk.</li>
            </ul>
          </Section>

          <Section title="11) Intellectual Property">
            <ul className="list-disc pl-5">
              <li>Flowman and its licensors own all rights to the Service and materials, excluding User Content.</li>
              <li>Feedback: If you provide suggestions, you grant Flowman a royalty-free, perpetual, irrevocable license to use them.</li>
            </ul>
          </Section>

          <Section title="12) Termination">
            <ul className="list-disc pl-5">
              <li>You may stop using the Service and request account deletion at any time (subject to workspace ownership and retention obligations).</li>
              <li>We may suspend or terminate access for violations, legal requirements, or to protect the Service or users.</li>
              <li>Certain provisions survive termination (e.g., IP, disclaimers, limitations of liability).</li>
            </ul>
          </Section>

          <Section title="13) Disclaimers">
            <p>The Service is provided “as is” and “as available” without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant uninterrupted, secure, or error-free operation.</p>
          </Section>

          <Section title="14) Limitation of Liability">
            <p>To the fullest extent permitted by law, Flowman will not be liable for any indirect, incidental, special, consequential, or exemplary damages, including lost profits, data, or goodwill. Our total liability for any claim is limited to the greater of (a) the amount you paid in the 12 months before the claim or (b) USD $100.</p>
          </Section>

          <Section title="15) Indemnification">
            <p>You will indemnify and hold Flowman, its affiliates, officers, agents, and employees harmless from any claim, loss, or expense (including attorneys’ fees) arising from your use of the Service, your User Content, or your violation of these Terms or law.</p>
          </Section>

          <Section title="16) Changes to the Terms">
            <p>We may update these Terms from time to time. The Effective Date will be updated accordingly. Continued use after changes become effective constitutes acceptance.</p>
          </Section>

          <Section title="17) Governing Law and Dispute Resolution">
            <p>These Terms are governed by the laws of [Jurisdiction], with disputes resolved exclusively in the courts of [Venue, Jurisdiction].</p>
          </Section>

          <Section title="18) Contact">
            <p>Questions? Contact us at [Contact Email].</p>
          </Section>

          <div className="mt-8 flex justify-end">
            <a href="/signup" className="rounded-md bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-4 py-2 text-sm font-semibold text-white shadow hover:opacity-95">Back to sign up</a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default Terms;

